<template>
  <Loader
    :value="initPromise"
    :message="$metaInfo.title"
    :class="[
      'post-mutate',
      `post-mutate__ref--${reference.ref}`,
      `post-mutate__model--${reference.onModel.toLowerCase()}`,
      { 'post-mutate--new': isNew },
    ]"
    :key="id"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>

        <template #right>
          <div class="row wrap g-1">
            <Preview v-if="!isNew && reference.onModel !== 'ModelTemplate'" :item="item"></Preview>

            <Actions placement="post" :query="{ post: id, 'kvass-draft': draft || showDraft }" />

            <ButtonComponent
              v-if="!showDraft || isNew"
              :label="saveState.save.label"
              :loading-label="$t('saving')"
              :success-label="$t('saved')"
              :icon="['fal', saveState.save.icon]"
              :theme="saveState.save.theme"
              :promise="promises.mutate"
              :class="{
                'elder-button--disabled': saveState.save.disabled,
                'elder-button--invalid': saveState.save.invalid,
              }"
              v-tooltip="{ content: saveState.save.tooltip }"
              @click="submit"
            />

            <Draft
              v-else
              v-model="draft"
              :saveState="saveState"
              :reference="{ ref: reference.ref, onModel: reference.onModel }"
              :submit="submit"
              @reset="() => fetch()"
            />
          </div>
        </template>
      </HeaderComponent>

      <Card tag="form" @submit.prevent="submit" class="post-mutate__form">
        <template #header>
          <FieldBuilder
            v-if="data && buildLayout"
            :value="data.layout || cfSchemas"
            @input="$ev => (data.layout = $ev)"
            :item="{
              ...data,
              theme,
              reference,
              config: config || $path('customFieldsConfig.0.source', data),
            }"
            :select-from-preset="true"
            :preset-title="collectionLabel"
            :customFieldSchemas="customFieldSchemas"
            :build="buildLayout"
            placement="post"
        /></template>
        <template #default>
          <div :class="['post-mutate__inputs', { 'post-mutate__inputs--add-grid': !fields.slots.length }]">
            <FieldRenderer v-for="item in fields.default" :validator="validator" :item="item" :value="data" />

            <CustomFields
              v-model="data.customFields"
              :schema="cfSchemas"
              :data="cfData"
              data-field="customFields"
              :validator="validator"
              validator-prefix="customFields"
            >
              <template v-for="item in fields.slots" v-slot:[item.slotKey]>
                <FieldRenderer :validator="validator" :item="item" :value="data" />
              </template>
            </CustomFields>
          </div>
        </template>
      </Card>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import UserApi from '@/api/User'
import Model from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import { assocPath, mergeDeepRight } from 'ramda'
import { DateTransformer } from '@/utils/transformers'
import { ToArray } from '@/utils'
import getMediaTypesFromTemplate from '@/utils/getMediaTypesFromTemplate'
import graphql from '@/api/graphql'
import MetadataQuery from '@/api/graphql/queries/PostMutateMetadata.gql'

import { InputComponent } from 'vue-elder-input'

import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card-v2'
import Actions from '@/components/Actions'

import DraftAlert from '@/components/Draft/Alert'
import Preview from '@/components/Preview'
import Draft from '@/components/Draft/Component'
import FieldBuilder from '@/components/FieldBuilder'
import { getCustomFieldsFromLayout } from '@/components/FieldBuilder/utils'
import FieldRenderer from '@/components/Post/StepsFields/FieldRenderer.vue'
import fields from '@/components/Post/StepsFields/fields.js'
import { buildStepsFields } from '@/utils'
import { getExtensions } from '@/components/Tiptap/Extensions'

let getLabel = (label, count = 1) => (label || '').split('|')[count > 1 ? 'pop' : 'shift']()

export default {
  mixins: [
    Validator({
      data: 'data',
      labels: 'labels',
      rules: 'rules',
    }),
    Model({
      source: 'item',
      model: {
        id: null,
        slug: null,
        title: null,
        content: null,
        media: {
          cover: null,
        },
        author: null,
        customFields: null,
        customFieldsConfig: [],
        publishedFrom: null,
        layout: null,
      },
      transform: {
        author: v => (v && v.id) || null,
        'customFieldsConfig.*.schema': undefined,
      },
      guard: true,
    }),
  ],
  props: {
    id: String,
    reference: Object,
    collection: String,
    config: String,
    returnTo: String,
  },
  provide() {
    const RichTextButtonOptions = {
      Project: [
        { value: '#brochure', label: this.$t('brochure') },
        { value: '#lead', label: this.$t('leadForm') },
        { value: '#flatfinder', label: this.$tc('unitSelector', 1) },
      ],
    }

    return {
      RichTextOptions: {
        commands: getExtensions(),
        button: {
          internalItems: `/api/resource/post?field.label=title&field.value=id&field.thumbnail=media.cover&references=${this.reference.ref}`,
          colors: ['primary', 'secondary', 'custom'],
          enableIcon: true,
          pages: this.reference.onModel === 'Page' ? [{ value: '#contact', label: this.$t('getInTouch') }] : [],
          anchorItems: RichTextButtonOptions[this.reference.onModel] || [],
        },
      },
    }
  },
  data() {
    return {
      metadata: null,
      initPromise: null,
      body: document.body,
      draft: true,
    }
  },
  watch: {
    id: {
      handler(id) {
        this.initPromise = this.init(id)
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Post', ['item', 'promises', 'states']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
    }),
    types() {
      return getMediaTypesFromTemplate(this.theme)
    },

    cfData() {
      return mergeDeepRight(this.item, { ...this.data, reference: this.reference })
    },

    labels() {
      return {
        ...mapi18n(['title', 'author', 'content', 'publishedFrom', 'slug', 'cover']),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        title: 'required|string',
        slug: 'slug',
        ...this.customFieldValidatorConfig.rules,
      }
    },
    publishedFrom: DateTransformer('data.publishedFrom'),
    isNew() {
      return !this.id
    },
    theme() {
      const value = this.collection.split('.')
      if (!value) return
      return value[0]
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },

    showDraft() {
      return this.$path('item.references.0.metadata.draftAvailable') || this.hasActiveDraft
    },

    metadataLabels() {
      return {
        collection: this.$path('metadata.PostCollection.label'),
        config: (
          (this.$path('metadata.PostCollection.schemas') || []).find(({ source }) => source === this.config) || {}
        ).label,
      }
    },
    collectionLabel() {
      return getLabel(this.metadataLabels.collection, 2)
    },

    breadcrumbs() {
      const kebab = this.reference.onModel.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

      return [
        {
          title: this.$tc('post', 2),
          condition: !this.reference,
        },
        {
          title: getLabel(this.metadataLabels.collection, 2),
          action: {
            name: `${kebab}-posts`,
            params: { id: this.reference.ref },
            query: { collection: this.collection },
          },
          condition: this.$path('metadata.PostCollection'),
        },
        { title: this.$metaInfo.title },
      ]
        .flat()
        .filter(Boolean)
        .filter(e => !('condition' in e) || e.condition)
    },
    customFieldSchemas() {
      if (!this.data) return []
      return this.data.customFieldsConfig.map(c => c.schema).flat()
    },
    buildLayout() {
      if ((this.config || this.$path('customFieldsConfig.0.source', this.data) || '').includes('custom-subpage'))
        return true
      return this.theme === 'plain-project'
    },
    fields() {
      return buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this)
    },

    cfSchemas() {
      if (!this.buildLayout) return this.customFieldSchemas
      let layout = this.data.layout

      if (!layout) {
        layout = this.customFieldSchemas.filter(
          i =>
            (this.$path('placement.resource', i) === 'post' || !this.$path('placement.resource', i)) &&
            [0, 1].includes(this.$path('block.limit.min', i)),
        )
      }

      return getCustomFieldsFromLayout(layout, this.customFieldSchemas, 'post')
    },

    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    saveState() {
      let base = {
        save: {
          label: this.$t(
            this.isNew ? (this.showDraft ? 'createDraft' : 'create') : this.showDraft ? 'saveDraft' : 'save',
          ),
          icon: this.isNew ? 'plus' : 'arrow-to-bottom',
          disabled: !this.validator.passes || !this.hasChanges,
          theme: this.showDraft ? 'secondary' : 'primary',
        },
        publish: {
          label: this.$t('publish'),
          icon: 'chevron-down',
          disabled: !this.validator.passes || (!this.hasChanges && !this.draft),
          theme: 'secondary',
        },
      }

      if (!this.states.fetchOne && !this.validator.passes) {
        const invalid = {
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

        Object.keys(base).forEach(key => {
          base[key] = { ...base[key], ...invalid }
        })
      }

      return base
    },
  },
  methods: {
    fetchUsers(filter) {
      return UserApi.get({ filter: { ...filter, type: 'user' } }, { query: 'UserSearch' })
    },
    getLabel,
    ...mapActions('Post', {
      fetch(dispatch) {
        if (!this.id) {
          const boilerplate = {
            ...(this.$path('item') || {}),
            customFieldsConfig: this.metadata.CustomFieldSchemas.filter(s => ToArray(this.config).includes(s.id)).map(
              s => ({
                source: s.id,
                schema: s.fields,
              }),
            ),
            customFields: this.metadata.CustomFieldSchemas.reduce((res, cur) => ({ ...res, ...cur.default }), {}),
          }

          return this.set(boilerplate)
        }
        return dispatch('fetch', [{ id: this.id }, { draft: this.draft }])
      },
      mutate: 'mutate',
      remove: 'remove',
    }),

    ...mapMutations('Post', ['set']),

    submit() {
      if (this.saveState.save.disabled || this.saveState.save.invalid) return

      let data = {
        ...this.getData({ diff: !this.isNew }),
        id: this.id,
      }

      if (this.isNew && this.reference) data.references = [this.reference]

      return this.mutate([
        data,
        {
          strategy: (item, payload) => {
            const { id, url, previewUrl, isPublished } = payload
            return mergeDeepRight(item, { ...this.data, id, url, previewUrl, isPublished })
          },
          draft: this.draft,
        },
      ]).then(data => {
        if (!this.isNew) return

        this.hasChanges = false // Change detector is to slow - forcing no change
        if (this.returnTo) return this.$router.push(this.returnTo)
        return this.$router.replace({
          name: 'post-edit',
          params: { id: data.id },
          query: this.$route.query,
        })
      })
    },
    async init(id) {
      await this.getMetadata()
      await this.fetch()
    },

    getMetadata() {
      let getCollection = Boolean(this.collection)
      let getCustomFieldSchemas = Boolean(this.config)

      const promises = []

      if (Boolean(this.reference)) {
        if (['ModelTemplate'].includes(this.reference.onModel)) {
          this.draft = false
        } else {
          promises.push(
            graphql
              .request(
                `
          query ($id: ID!) {
            ${this.reference.onModel}(id: $id) {
              draftId
              metadata {
                draftAvailable
              }
            }
          }`,
                {
                  id: this.reference.ref,
                },
                { draft: true },
              )
              .then(data => {
                const draftBoilerPlate = {
                  references: [data[this.reference.onModel]],
                }

                this.set(draftBoilerPlate)

                const setDraft = () => {
                  if (this.$path('item.references.0.draftId')) return this.$path('item.references.0.draftId')
                  return this.isNew ? this.$path('item.references.0.metadata.draftAvailable') : false
                }

                this.draft = setDraft()
              }),
          )
        }
      }

      if (getCollection || getCustomFieldSchemas) {
        promises.push(
          graphql
            .request(MetadataQuery, {
              getCollection,
              collection: this.collection,
              getCustomFieldSchemas,
              customFieldSchemas: ToArray(this.config),
            })
            .then(metadata => (this.metadata = metadata)),
        )
      }

      return Promise.all(promises)
    },
    removeCustomField(item) {
      this.data.customFields = assocPath(item.key.split('.'), null, this.data.customFields)
      this.data.customFieldsConfig = this.data.customFieldsConfig.filter(c => c.source !== item.source)
    },
  },
  metaInfo() {
    return {
      title: this.isNew
        ? this.$t('createResource', {
            resource: (getLabel(this.metadataLabels.config) || this.$tc('post')).toLowerCase(),
          })
        : this.item.title || this.i18n.loading,
    }
  },

  components: {
    InputComponent,
    HeaderComponent,
    Card,
    CustomFields,
    Actions,
    DraftAlert,
    Preview,
    Draft,
    FieldBuilder,
    FieldRenderer,
  },
}
</script>
<style lang="scss">
.post-mutate {
  background-color: $background-grey;

  // &__model--modeltemplate {
  //   [data-key='flatfinder-subpage'] {
  //     display: none !important;
  //   }
  // }

  .view .card-v2 {
    &__header {
      flex-direction: row;
      justify-content: right;
      padding-bottom: 0;
    }
  }

  &__form {
    max-width: 1200px;
    margin: 0 auto;
  }

  &__inputs {
    --columns: 1;
    display: grid;
    grid-gap: $form-gap;
    margin-top: 2rem;

    grid-template-columns: repeat(2, 1fr);
    grid-gap: math.div($form-gap-row, 2) $form-gap-column;
    [data-field='customFields'] {
      grid-column-end: span 2;
    }

    &--add-grid {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-areas:
        'title title cover cover'
        'content content cover cover'
        'content content publishedFrom slug'
        'content content author author'
        'customFields customFields customFields customFields';

      grid-template-rows: auto auto auto 1fr;
      grid-auto-rows: auto;

      align-items: start;
      $fields: cover, title, slug, customFields, publishedFrom, author, content;

      @each $field in $fields {
        [data-field='#{$field}'] {
          grid-area: $field;
        }
      }
    }
  }

  [data-field='cover'] {
    aspect-ratio: 6 / 4;
    margin-bottom: 0.5rem;
  }

  [data-field='content'] {
    align-self: stretch;
  }

  &--new {
    .post-mutate__field-render-item-default {
      grid-template-areas:
        'title title cover cover'
        'content content cover cover'
        'content content publishedFrom publishedFrom'
        'customFields customFields customFields customFields';
    }
  }

  .custom-fields {
    margin-top: 0;
  }

  [type='submit'] {
    margin-right: auto;
  }
}
</style>
